import React, { useRef, useEffect, useState, useCallback, useMemo, useSyncExternalStore, useImperativeHandle } from 'react';
import { Bot, User, ChevronLeft, ChevronRight, ChevronDown, Send, Clock, Check, AlertCircle, Code, X, Download, UserCheck, Loader2 } from 'lucide-react';
import { authService } from '../services/authService';
import { formatZonedTime, formatZonedDate, formatZonedDateTime, formatRemainingTime, groupKeyForZonedDate, parseServerTimestamp } from '../services/dateService';
import { leadEventBus } from '../services/leadEventBus';
import { getMessageIdsWithVisibleTime } from '../services/messageTimestamps';
import { hasAiReplySince, TRIGGERED_AI_REPLY_TIMEOUT_MS } from '../services/aiReplyWait';
import SearchableDropdown from './SearchableDropdown';

// Shared conversation state by lead ID.
// This survives React StrictMode effect cleanup/remount cycles and parent remounts,
// so an API response can still update the currently mounted ChatBox instance.
const conversationStore = new Map();

const getConversationEntry = (leadId) => {
  if (!conversationStore.has(leadId)) {
    conversationStore.set(leadId, {
      data: [],
      error: null,
      isLoading: false,
      lastFetchTime: 0,
      promise: null,
      subscribers: new Set()
    });
  }

  return conversationStore.get(leadId);
};

const notifyConversationSubscribers = (entry) => {
  entry.subscribers.forEach((subscriber) => {
    subscriber({
      data: entry.data,
      error: entry.error,
      isLoading: entry.isLoading
    });
  });
};

const fetchConversationForLead = async (leadId, { force = false } = {}) => {
  if (!leadId) return [];

  const entry = getConversationEntry(leadId);
  const now = Date.now();

  if (entry.isLoading && entry.promise) {
    console.log(`⏭️ Reusing in-flight fetch for lead ${leadId}`);
    return entry.promise;
  }

  if (!force && now - entry.lastFetchTime < 5000) {
    console.log('⏭️ Skipping fetch - rate limited');
    return entry.data;
  }

  entry.isLoading = true;
  entry.error = null;
  entry.lastFetchTime = now;
  notifyConversationSubscribers(entry);

  const startTime = Date.now();
  const callId = Math.random().toString(36).substr(2, 9);
  console.log(`🔄 Shared fetch for lead: ${leadId} [${callId}]`);

  entry.promise = import('../services/api')
    .then(({ getConversationByLeadId }) => getConversationByLeadId(leadId))
    .then((conversationData) => {
      const safeData = Array.isArray(conversationData) ? conversationData : [];
      entry.data = safeData;
      entry.error = null;
      console.log(`✅ Shared fetch completed in ${Date.now() - startTime}ms, data: ${safeData.length} messages [${callId}]`);
      notifyConversationSubscribers(entry);
      return safeData;
    })
    .catch((error) => {
      entry.error = error;
      console.error(`❌ Shared fetch failed after ${Date.now() - startTime}ms:`, error);
      notifyConversationSubscribers(entry);
      throw error;
    })
    .finally(() => {
      entry.isLoading = false;
      entry.promise = null;
      notifyConversationSubscribers(entry);
    });

  return entry.promise;
};

// Shared one-second ticker for live countdowns. Only runs while at least one
// component is mounted, so message countdowns tick without re-rendering ChatBox.
const liveCountdownStore = {
  value: Date.now(),
  listeners: new Set(),
  intervalId: null,

  subscribe(listener) {
    this.listeners.add(listener);
    if (this.listeners.size === 1 && !this.intervalId) {
      // Refresh immediately — value may be stale since module load.
      this.value = Date.now();
      this.intervalId = setInterval(() => {
        this.value = Date.now();
        this.listeners.forEach((l) => l());
      }, 1000);
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    };
  },

  getValue() {
    return this.value;
  },
};

const subscribeToLiveCountdown = (listener) => liveCountdownStore.subscribe(listener);
const getLiveCountdown = () => liveCountdownStore.getValue();

const useLiveNow = () => useSyncExternalStore(
  subscribeToLiveCountdown,
  getLiveCountdown
);

const LogModeMessageStatus = ({ scheduledSendTime, logModeStatus, isSimulated, messageId, isAdmin, onLogModeAction }) => {
  if (logModeStatus === 'VALIDATED' || logModeStatus === 'AUTO_VALIDATED') {
    return isSimulated ? (
      <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
        <Check size={8} className="mr-1" />
        Simulated
      </span>
    ) : (
      <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
        <Check size={8} className="mr-1" />
        Sent
      </span>
    );
  }

  if (logModeStatus === 'INVALIDATED') {
    return (
      <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
        <X size={8} className="mr-1" />
        Unscheduled
      </span>
    );
  }

  return (
    <LogModeCountdown
      scheduledSendTime={scheduledSendTime}
      messageId={messageId}
      isAdmin={isAdmin}
      onLogModeAction={onLogModeAction}
    />
  );
};

const LogModeCountdown = ({ scheduledSendTime, messageId, isAdmin, onLogModeAction }) => {
  const now = useLiveNow();
  const [pendingAction, setPendingAction] = useState(null);
  const scheduledDate = parseServerTimestamp(scheduledSendTime);
  const overdueMs = scheduledDate ? now - scheduledDate.getTime() : null;
  const isOverdue = overdueMs !== null && overdueMs > 0;

  const runAction = useCallback(async (action) => {
    setPendingAction(action);
    try {
      await onLogModeAction(messageId, action);
    } finally {
      setPendingAction(null);
    }
  }, [messageId, onLogModeAction]);

  // No auto-send fallback here: the Quartz job is the single sender. If it is
  // lost (e.g. restart with RAMJobStore) the message shows "Overdue" and an
  // admin can still send it manually via the buttons below.
  return (
    <div className="flex items-center gap-1">
      {isOverdue && !pendingAction ? (
        <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
          <AlertCircle size={8} className="mr-1" />
          Overdue
        </span>
      ) : (
      <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
        {pendingAction ? (
          <>
            <Loader2 size={8} className="mr-1 animate-spin" />
            {pendingAction === 'send' ? 'Sending…' : 'Unscheduling…'}
          </>
        ) : (
          <>
            <Clock size={8} className="mr-1" />
            {formatRemainingTime(scheduledSendTime, new Date(now), { includeSeconds: true })}
          </>
        )}
      </span>
      )}
      {isAdmin && (
        <>
          <button
            type="button"
            onClick={() => runAction('send')}
            disabled={pendingAction !== null}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded-full flex items-center shadow"
            title="Send now"
          >
            {pendingAction === 'send' ? <Loader2 size={8} className="animate-spin" /> : <Send size={8} />}
          </button>
          <button
            type="button"
            onClick={() => runAction('cancel')}
            disabled={pendingAction !== null}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded-full flex items-center shadow"
            title="Unschedule"
          >
            {pendingAction === 'cancel' ? <Loader2 size={8} className="animate-spin" /> : <X size={8} />}
          </button>
        </>
      )}
    </div>
  );
};

const ChatBox = React.forwardRef(({ scrollToBottomOnLoad, selectedLead, onMessageSent, leadId: propLeadId, storeTimeZone, onReloadStateChange, onAiStateChange, awaitingAiReplySince = null, onAiReplyWaitDone }, ref) => {
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const aiThinkingTimeoutRef = useRef(null);
  const [selectedN2Payload, setSelectedN2Payload] = useState(null);
  const [selectedHandoffError, setSelectedHandoffError] = useState(null);
  const [showManualHandoffModal, setShowManualHandoffModal] = useState(false);
  const [handoffDescription, setHandoffDescription] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [includeTranscript, setIncludeTranscript] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [sendToAsd, setSendToAsd] = useState(false);
  const [isProcessingHandoff, setIsProcessingHandoff] = useState(false);
  const [n2Enabled, setN2Enabled] = useState(true);
  const [isTogglingN2, setIsTogglingN2] = useState(false);
  const [sendRealMessage, setSendRealMessage] = useState(() =>
    selectedLead?.sendRealMessage !== false
  );
  //Clicking it scrolls to bottom and dismisses it.
  const [pendingNewMessages, setPendingNewMessages] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [showScheduledTasksModal, setShowScheduledTasksModal] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const previousSelectedLeadRef = useRef(selectedLead);
  const previousConversationsRef = useRef([]);
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  const scrollPositionRef = useRef(0);
  const messagesContainerRef = useRef(null);
  const fetchedN2StatusForLeadRef = useRef(null);
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [intents, setIntents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [proposedMessage, setProposedMessage] = useState(null);
  const [isLoadingProposedMessage, setIsLoadingProposedMessage] = useState(false);
  const [isSendingTemplateMessage, setIsSendingTemplateMessage] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateN2Payload, setTemplateN2Payload] = useState(null);
  const [inlineError, setInlineError] = useState(null);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');

  // Extract the lead ID from prop or selectedLead
  const leadId = propLeadId !== undefined ? propLeadId : selectedLead?.id;
  const isAdmin = authService.isAdmin();

  const filteredIntents = useMemo(() => {
    if (!selectedCategory) return intents;
    return intents.filter((intent) => intent.category === selectedCategory);
  }, [intents, selectedCategory]);

  const categoryOptions = useMemo(() => [
    { value: '', label: 'All Categories' },
    ...categories.map((category) => ({ value: category, label: category }))
  ], [categories]);


  useEffect(() => {
    if (scrollToBottomOnLoad) {
      scrollToBottom();
    }
  }, [scrollToBottomOnLoad]);

  useEffect(() => {
    console.log('Conversations useEffect triggered:', {
      conversationsLength: conversations.length,
      optimisticMessagesLength: optimisticMessages.length,
      allMessagesLength: [...conversations, ...optimisticMessages].length
    });

    // Initialize previousConversationsRef if it's empty and conversations exist
    // Also scroll to bottom on initial load (first time we get messages)
    if (previousConversationsRef.current.length === 0 && conversations.length > 0) {
      previousConversationsRef.current = conversations;
      console.log('Initialized previousConversationsRef with', conversations.length, 'messages');
      setTimeout(() => scrollToBottom(true), 100);
      return;
    }

    const prevCount = previousConversationsRef.current.length;
    const currentCount = conversations.length;

    console.log('Message count comparison:', { prevCount, currentCount, hasNewMessages: currentCount > prevCount });

    if (currentCount > prevCount) {
      const newCount = currentCount - prevCount;
      if (isUserNearBottom()) {
        console.log('✨ New messages detected, scrolling to bottom');
        setTimeout(() => scrollToBottom(true), 100);
      } else {
        console.log('✨ New messages detected while scrolled up, showing pill');
        setPendingNewMessages((prev) => prev + newCount);
      }
    }
    if (isAiThinking) {
      const prevIds = new Set(previousConversationsRef.current.map((msg) => msg.id));
      const gotAiReply = conversations.some((msg) => !prevIds.has(msg.id) && msg.sender !== 'user');
      if (gotAiReply) {
        setIsAiThinking(false);
        if (aiThinkingTimeoutRef.current) {
          clearTimeout(aiThinkingTimeoutRef.current);
          aiThinkingTimeoutRef.current = null;
        }
      }
    }

    previousConversationsRef.current = conversations;
  }, [conversations, isAiThinking]);
 useEffect(() => {
    if (scrollToBottomOnLoad) {
      scrollToBottom();
    }
  }, [scrollToBottomOnLoad]);
  // Auto-scroll when optimistic messages are added
  useEffect(() => {
    if (optimisticMessages.length > 0) {
      scrollToBottom(true);
    }
  }, [optimisticMessages]);

  useEffect(() => {
    if (isAiThinking) {
      scrollToBottom(true);
    }
  }, [isAiThinking]);

  // Debug conversation prop changes
  useEffect(() => {
    console.log('Conversations prop updated:', {
      length: conversations.length,
      conversations: conversations.map(c => ({ id: c.id, message: c.message.substring(0, 50) }))
    });
  }, [conversations]);

  // Debug loading state changes
  useEffect(() => {
    console.log('Loading state changed:', isLoadingConversations);
  }, [isLoadingConversations]);

  // Debug optimistic messages changes
  useEffect(() => {
    console.log('Optimistic messages updated:', optimisticMessages.length);
  }, [optimisticMessages]);

  useEffect(() => {
    // Clear input only when lead actually changes, not during conversation reloads
    if (previousSelectedLeadRef.current?.id !== leadId) {
      console.log('🔀 Lead changed, clearing message input');
      setMessageInput('');
      setPendingNewMessages(0);
      if (aiThinkingTimeoutRef.current) {
        clearTimeout(aiThinkingTimeoutRef.current);
        aiThinkingTimeoutRef.current = null;
      }
      setIsAiThinking(false);
      previousSelectedLeadRef.current = selectedLead;
    }
  }, [leadId, selectedLead]);

  useEffect(() => () => {
    if (aiThinkingTimeoutRef.current) clearTimeout(aiThinkingTimeoutRef.current);
  }, []);


  const onAiReplyWaitDoneRef = useRef(onAiReplyWaitDone);
  useEffect(() => {
    onAiReplyWaitDoneRef.current = onAiReplyWaitDone;
  }, [onAiReplyWaitDone]);

  const finishAwaitingAiReply = useCallback(() => {
    if (aiThinkingTimeoutRef.current) {
      clearTimeout(aiThinkingTimeoutRef.current);
      aiThinkingTimeoutRef.current = null;
    }
    setIsAiThinking(false);
    onAiReplyWaitDoneRef.current?.();
  }, []);

  useEffect(() => {
    if (!awaitingAiReplySince) return;
    const remainingMs = awaitingAiReplySince + TRIGGERED_AI_REPLY_TIMEOUT_MS - Date.now();
    if (remainingMs <= 0) {
      finishAwaitingAiReply();
      return;
    }
    setIsAiThinking(true);
    if (aiThinkingTimeoutRef.current) clearTimeout(aiThinkingTimeoutRef.current);
    aiThinkingTimeoutRef.current = setTimeout(finishAwaitingAiReply, remainingMs);
  }, [awaitingAiReplySince, finishAwaitingAiReply]);

  useEffect(() => {
    if (!awaitingAiReplySince) return;
    if (!n2Enabled || hasAiReplySince(conversations, awaitingAiReplySince)) {
      finishAwaitingAiReply();
    }
  }, [awaitingAiReplySince, conversations, n2Enabled, finishAwaitingAiReply]);

  // Fetch N2 and real-sending status from backend when leadId changes
  useEffect(() => {
    if (leadId && fetchedN2StatusForLeadRef.current !== leadId) {
      fetchedN2StatusForLeadRef.current = leadId;
      const fetchLeadStatus = async () => {
        try {
          const { api } = await import('../services/api');
          const response = await api.get(`/leads/${leadId}`);
          const n2Status = response.data.n2Enabled !== false;
          const realSmsStatus = response.data.sendRealMessage !== false;
          setN2Enabled(n2Status);
          setSendRealMessage(realSmsStatus);
          console.log('🤖 Fetched lead status from backend for lead', leadId, ':', { n2Status, realSmsStatus });
        } catch (error) {
          console.error('Error fetching lead status:', error);
          // Fall back to prop values if API call fails
          if (selectedLead?.n2Enabled !== undefined) {
            setN2Enabled(selectedLead.n2Enabled);
          } else {
            setN2Enabled(true); // Default to true
          }
          if (selectedLead?.sendRealMessage !== undefined) {
            setSendRealMessage(selectedLead.sendRealMessage);
          } else {
            setSendRealMessage(true); // Default to true
          }
        }
      };
      fetchLeadStatus();
    }
  }, [leadId]);

  // Fetch scheduled tasks for the current lead (admin only)
  useEffect(() => {
    if (leadId && isAdmin) {
      const fetchScheduledTasks = async () => {
        try {
          const { api } = await import('../services/api');
          const response = await api.get(`/admin/scheduled-tasks/lead/${leadId}`);
          setScheduledTasks(response.data);
        } catch (error) {
          console.error('Error fetching scheduled tasks:', error);
          setScheduledTasks([]);
        }
      };
      fetchScheduledTasks();
    } else {
      setScheduledTasks([]);
    }
  }, [leadId, isAdmin]);

  // Fetch enabled intents when intent modal opens
  useEffect(() => {
    if (showIntentModal && isAdmin) {
      const fetchIntents = async () => {
        try {
          const { api } = await import('../services/api');
          const storeId = selectedLead?.storeIdFk;
          const response = await api.get('/admin/intents/enabled', {
            params: storeId ? { storeId } : {}
          });
          const allIntents = Array.isArray(response.data) ? response.data : [];
          setIntents(allIntents);
          const uniqueCategories = [...new Set(allIntents.map(intent => intent.category).filter(Boolean))].sort();
          setCategories(uniqueCategories);
        } catch (error) {
          console.error('Error fetching intents:', error);
          setIntents([]);
          setCategories([]);
        }
      };
      fetchIntents();
    }
  }, [showIntentModal, isAdmin, selectedLead?.storeIdFk]);

  // Keep a live clock for scheduled task remaining-time labels
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  // Subscribe to shared conversation state and keep polling active for this lead.
  // Important: cleanup only unsubscribes this component. It does not cancel an in-flight
  // request, so a first response that arrives after a remount is cached and delivered.
  useEffect(() => {
    if (!leadId) {
      setConversations([]);
      setIsLoadingConversations(false);
      return;
    }

    const entry = getConversationEntry(leadId);
    let isSubscribed = true;

    const syncFromStore = ({ data, isLoading }) => {
      if (!isSubscribed) return;
      const conversationData = Array.isArray(data) ? data : [];
      setConversations(conversationData);
      setIsLoadingConversations(Boolean(isLoading));

      // Remove optimistic messages that have been confirmed by backend
      setOptimisticMessages(prev => {
        // Match against ALL backend messages (sender + message content)
        const backendMessageSet = new Set(
          conversationData.map(msg => `${msg.sender}|${msg.message}`)
        );
        return prev.filter(opt => !backendMessageSet.has(`${opt.sender}|${opt.message}`));
      });
    };

    entry.subscribers.add(syncFromStore);
    syncFromStore(entry);

    console.log(`🚀 Subscribed to conversations for lead: ${leadId} [${componentId.current}]`);

    fetchConversationForLead(leadId, { force: entry.data.length === 0 }).catch(() => {
      // Error is already stored/logged in fetchConversationForLead.
    });

    return () => {
      console.log(`🧹 Unsubscribing from conversations for lead ${leadId}: ${componentId.current}`);
      isSubscribed = false;
      entry.subscribers.delete(syncFromStore);
    };
  }, [leadId]);

  // Real-time refresh: instead of polling every 20s, stay idle and only
  // refetch this conversation when the backend actually pushes a NEW_MESSAGE event for this specific lead over the shared SSE stream
  useEffect(() => {
    if (!leadId) return undefined;

    const unsubscribe = leadEventBus.subscribe((event) => {
      if (event?.type === 'NEW_MESSAGE' && event.leadId === leadId) {
        fetchConversationForLead(leadId, { force: true }).catch(() => {
          console.error('Failed to fetch updated conversation for lead:', leadId);
        });
      }
    });

    return unsubscribe;
  }, [leadId]);

  // Is the user currently within 50px of the bottom of the messages list?
  const isUserNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 50;
  };

  const scrollToBottom = (forceScroll = false) => {
    if (messagesContainerRef.current) {
      const isNearBottom = isUserNearBottom();

      // Scroll if: forced, near bottom, or on initial load
      if (forceScroll || isNearBottom || scrollToBottomOnLoad) {
        console.log('📍 Scrolling to bottom - force:', forceScroll, 'nearBottom:', isNearBottom, 'onLoad:', scrollToBottomOnLoad);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      } else {
        console.log('📍 Not scrolling - user is scrolled up, preserving position');
      }
    }
  };

  const formatTime = (timestamp) => formatZonedTime(timestamp, storeTimeZone);

  const formatDate = (timestamp) => formatZonedDate(timestamp, storeTimeZone);

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((message) => {
      const key = groupKeyForZonedDate(message.timestamp, storeTimeZone);
      if (!groups[key]) groups[key] = [];
      groups[key].push(message);
    });
    return groups;
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedLead || isSending) {
      console.log('Send message blocked:', {
        hasMessage: !!messageInput.trim(),
        hasLead: !!selectedLead,
        isSending
      });
      return;
    }

    const messageText = messageInput.trim();
    const tempId = `temp-${Date.now()}`;

    console.log('Sending message:', { messageText, tempId, leadId: selectedLead.id });

    // Create optimistic message
    const optimisticMessage = {
      id: tempId,
      message: messageText,
      sender: 'user',
      timestamp: new Date().toISOString(),
      status: 'sending',
      isOptimistic: true
    };

    // Add optimistic message immediately
    console.log('Adding optimistic message:', optimisticMessage);
    setOptimisticMessages(prev => [...prev, optimisticMessage]);
    setMessageInput('');
    setIsSending(true);

    try {
      // Import the API function
      const { simulateCustomerMessage } = await import('../services/api');

      console.log('Calling simulateCustomerMessage API...', { sendRealMessage });
      await simulateCustomerMessage(selectedLead.id, messageText, sendRealMessage);

      console.log('API call successful, waiting for saved message');

      if (n2Enabled) {
        setIsAiThinking(true);
        if (aiThinkingTimeoutRef.current) clearTimeout(aiThinkingTimeoutRef.current);
        aiThinkingTimeoutRef.current = setTimeout(() => {
          setIsAiThinking(false);
          aiThinkingTimeoutRef.current = null;
        }, 45000);
      }

      setTimeout(() => {
        console.log('Checking if optimistic message still pending after 2 minutes');
        setOptimisticMessages(prev => prev.filter(msg => msg.id !== tempId));
      }, 120000);

      console.log('Simulated customer message sent successfully');
    } catch (error) {
      console.error('Error sending simulated message:', error);

      // Send failed outright — no reply is coming, so drop the thinking indicator.
      if (aiThinkingTimeoutRef.current) {
        clearTimeout(aiThinkingTimeoutRef.current);
        aiThinkingTimeoutRef.current = null;
      }
      setIsAiThinking(false);

      // Update optimistic message status to failed
      setOptimisticMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? { ...msg, status: 'failed' }
            : msg
        )
      );

      // Remove failed message after showing error state
      setTimeout(() => {
        setOptimisticMessages(prev => prev.filter(msg => msg.id !== tempId));
      }, 10000);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Combine real conversations with optimistic messages
  // Real messages sorted by timestamp, optimistic messages appended at end
  const allMessages = useMemo(() => {
    const realMessages = conversations.sort((a, b) =>
      new Date(a.timestamp) - new Date(b.timestamp)
    );
    const combined = [...realMessages, ...optimisticMessages];
    console.log('📊 allMessages recalculated:', combined.length, 'messages');
    return combined;
  }, [conversations, optimisticMessages]);

  const messageGroups = useMemo(() => {
    return groupMessagesByDate(allMessages);
  }, [allMessages]);

  const messageIdsWithVisibleTime = useMemo(() => {
      if (isAdmin) return new Set(allMessages.map((message) => message.id));
    return getMessageIdsWithVisibleTime(allMessages);
  }, [allMessages, isAdmin]);
    //"scroll to bottom" button's visibility stays
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAtBottom(isUserNearBottom());
    }, 50);
    return () => clearTimeout(timer);
  }, [allMessages]);

  const n2PayloadMessages = useMemo(() => {
    return allMessages.filter((message) => message.n2RequestJson || message.n2ResponseJson);
  }, [allMessages]);

  const nextScheduledTask = useMemo(() => {
    if (!scheduledTasks.length) return null;
    const activeTasks = scheduledTasks.filter(
      (task) => task.status === 'WAITING' || task.status === 'PAUSED' || task.status === 'ACQUIRED'
    );
    if (!activeTasks.length) return null;
    return activeTasks.sort((a, b) =>
      new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime()
    )[0];
  }, [scheduledTasks]);

  const formatJsonPayload = (payload) => {
    if (!payload) return '';
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch (error) {
      return payload;
    }
  };

  const parseJsonPayload = (payload) => {
    if (!payload) return null;
    try {
      return JSON.parse(payload);
    } catch (error) {
      return payload;
    }
  };

  const handleDownloadN2Payloads = () => {
    if (n2PayloadMessages.length === 0) {
      setInlineError('No N2 request/response payloads are available for this lead yet.');
      return;
    }

    const payload = {
      leadId,
      lead: selectedLead ? {
        id: selectedLead.id,
        customerName: selectedLead.customerName,
        phoneNumber: selectedLead.phoneNumber,
        status: selectedLead.status
      } : null,
      exportedAt: new Date().toISOString(),
      messages: n2PayloadMessages.map((message) => ({
        n2Request: parseJsonPayload(message.n2RequestJson),
        n2Response: parseJsonPayload(message.n2ResponseJson)
      }))
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lead-${leadId || 'unknown'}-n2-payloads.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTranscript = async () => {
    if (!leadId) {
      setInlineError('No lead ID available for transcript download.');
      return;
    }

    try {
      const { api } = await import('../services/api');
      const response = await api.get(`/transcript/${leadId}/pdf`, {
        responseType: 'blob'
      });

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transcript-lead-${leadId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading transcript:', error);
      setInlineError('Failed to download transcript. Please try again.');
    }
  };

  const handleManualReload = () => {
    if (!leadId) return;
    fetchConversationForLead(leadId, { force: true }).catch(() => {
      setInlineError('Failed to reload messages. Please try again.');
    });
  };

  // Let a parent (e.g. the widget's top header bar) trigger a reload and
  // know when a reload is in flight, without duplicating the fetch logic.
  // Filled in below once handleToggleN2 is defined, so the parent header can
  // own the AI switch while the toggle logic stays in one place here.
  const toggleAiRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reload: handleManualReload,
    isReloading: isLoadingConversations,
    toggleAi: () => toggleAiRef.current?.()
  }), [handleManualReload, isLoadingConversations]);

  useEffect(() => {
    onReloadStateChange?.(isLoadingConversations);
  }, [isLoadingConversations, onReloadStateChange]);

  useEffect(() => {
    onAiStateChange?.({ enabled: n2Enabled, toggling: isTogglingN2 });
  }, [n2Enabled, isTogglingN2, onAiStateChange]);

  // When a parent renders the AI switch (the widget header), don't repeat it
  // in the footer.
  const aiToggleInHeader = typeof onAiStateChange === 'function';

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);

  useEffect(() => {
    if (!showDownloadMenu) return undefined;
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const handleManualHandoff = async () => {
    if (!leadId) {
      setInlineError('Missing lead information for the handoff.');
      return;
    }

    setIsProcessingHandoff(true);
    try {
      const { api } = await import('../services/api');
      const response = await api.post('/manual-handoff', {
        leadId,
        description: handoffDescription,
        sendEmail,
        includeTranscript,
        sendSms,
        sendToAsd
      });

      console.log('Manual handoff successful:', response.data);

      // The ASD escalation can be a no-op (no ASD contacts, or no SMTP config
      // for the store) while the rest of the handoff still succeeds - say so
      // rather than letting it fail silently.
      if (sendToAsd && response.data?.asdWarning) {
        setInlineError(response.data.asdWarning);
      }

      // Reset modal state
      setHandoffDescription('');
      setSendEmail(true);
      setIncludeTranscript(true);
      setSendSms(true);
      setSendToAsd(false);
      setShowManualHandoffModal(false);

      // Refresh conversation to show new handoff message
      fetchConversationForLead(leadId, { force: true }).catch(() => {
        // Error is already stored/logged in fetchConversationForLead.
      });
    } catch (error) {
      console.error('Error processing manual handoff:', error);
      setInlineError('Failed to process manual handoff. Please try again.');
    } finally {
      setIsProcessingHandoff(false);
    }
  };

  const handleEmailCheckboxChange = (checked) => {
    setSendEmail(checked);
    // If email is unchecked, also uncheck transcript
    if (!checked) {
      setIncludeTranscript(false);
    }
  };

  const handleTranscriptCheckboxChange = (checked) => {
    setIncludeTranscript(checked);
    // If transcript is checked, also check email
    if (checked) {
      setSendEmail(true);
    }
  };

  const handleToggleN2 = async () => {
    if (!leadId) return;

    setIsTogglingN2(true);
    try {
      const { api } = await import('../services/api');
      const newStatus = !n2Enabled;

      const response = await api.put(`/leads/${leadId}/n2-status`, {
        n2Enabled: newStatus
      });

      console.log('N2 status toggled successfully:', response.data);
      setN2Enabled(newStatus);

      // Fetch updated lead data to ensure consistency
      try {
        const leadResponse = await api.get(`/leads/${leadId}`);
        const updatedLead = leadResponse.data;
        console.log('Fetched updated lead data:', updatedLead.n2Enabled);
        // Update local state with the confirmed value from backend
        if (updatedLead.n2Enabled !== undefined) {
          setN2Enabled(updatedLead.n2Enabled);
        }
      } catch (refreshError) {
        console.error('Error refreshing lead data after toggle:', refreshError);
        // Keep the local state change even if refresh fails
      }
    } catch (error) {
      console.error('Error toggling N2 status:', error);
    } finally {
      setIsTogglingN2(false);
    }
  };
  toggleAiRef.current = handleToggleN2;

  const handleToggleRealSms = async () => {
    const newValue = !sendRealMessage;
    if (newValue) {
      setShowGoLiveModal(true);
      return;
    }
    setSendRealMessage(newValue);

    if (selectedLead?.id) {
      try {
        const { updateLeadRealSmsStatus } = await import('../services/api');
        await updateLeadRealSmsStatus(selectedLead.id, newValue);
      } catch (error) {
        console.error('Failed to update lead real-sms status:', error);
        setSendRealMessage(!newValue);
      }
    }
  };

  const handleConfirmGoLive = async () => {
    setShowGoLiveModal(false);
    setSendRealMessage(true);

    if (selectedLead?.id) {
      try {
        const { updateLeadRealSmsStatus } = await import('../services/api');
        await updateLeadRealSmsStatus(selectedLead.id, true);
      } catch (error) {
        console.error('Failed to update lead real-sms status:', error);
        setSendRealMessage(false);
      }
    }
  };

  const handleCancelScheduledTask = async (task) => {
    if (!leadId) return;

    try {
      const { api } = await import('../services/api');
      if (task.jobGroup === 'log-mode-jobs' && task.messageId) {
        // Use the log-mode endpoint so the message is marked INVALIDATED,
        // not just the Quartz job deleted.
        await api.post(`/messages/${task.messageId}/log-mode/cancel`);
        fetchConversationForLead(leadId, { force: true }).catch(() => {});
      } else {
        await api.post(`/admin/scheduled-tasks/${task.jobKey}/cancel`, { jobGroup: task.jobGroup || 'cancel-appointment-jobs' });
      }
      // Refresh scheduled tasks
      const response = await api.get(`/admin/scheduled-tasks/lead/${leadId}`);
      setScheduledTasks(response.data);
    } catch (error) {
      console.error('Error cancelling scheduled task:', error);
      setInlineError('Failed to cancel scheduled task');
    }
  };

  const handleLogModeAction = useCallback(async (messageId, action) => {
    try {
      const { api } = await import('../services/api');
      await api.post(`/messages/${messageId}/log-mode/${action}`);
      // Await the refresh so the loading state only clears once the updated
      // logModeStatus has arrived — otherwise the buttons re-enable briefly.
      try {
        await fetchConversationForLead(leadId, { force: true });
      } catch (refreshError) {
        console.error('Failed to refresh conversation after log-mode action:', refreshError);
      }
    } catch (error) {
      console.error(`Error ${action === 'send' ? 'sending' : 'cancelling'} log-mode message:`, error);
      setInlineError(action === 'send' ? 'Failed to send message' : 'Failed to cancel message');
    }
  }, [leadId]);

  const handleCloseIntentModal = () => {
    setShowIntentModal(false);
    setProposedMessage(null);
    setSelectedIntent(null);
    setTemplateError(null);
    setTemplateN2Payload(null);
    setSelectedCategory('');
    setIsEditingTemplate(false);
    setEditedMessage('');
  };

  const handleCategorySelect = (option) => {
    const category = option ? option.value || '' : '';
    setSelectedCategory(category);
    setSelectedIntent(null);
    setProposedMessage(null);
    setTemplateError(null);
    setTemplateN2Payload(null);
  };

  const handleIntentSelect = (intent) => {
    if (!intent) {
      setSelectedIntent(null);
      setProposedMessage(null);
      setTemplateError(null);
      setTemplateN2Payload(null);
      return;
    }
    handleGetTemplateResponse(intent);
  };

  const handleGetTemplateResponse = async (intent) => {
    if (!leadId) return;

    setIsLoadingProposedMessage(true);
    setSelectedIntent(intent);
    setProposedMessage(null);
    setTemplateError(null);
    setTemplateN2Payload(null);

    try {
      const { api } = await import('../services/api');
      const response = await api.post('/template-response', {
        leadId,
        intent: intent.intentName
      });
      if (response.data.n2RequestJson || response.data.n2ResponseJson) {
        setTemplateN2Payload({
          n2RequestJson: response.data.n2RequestJson,
          n2ResponseJson: response.data.n2ResponseJson
        });
      }

      if (response.data.success && response.data.assistantMessage) {
        setProposedMessage(response.data.assistantMessage);
        setEditedMessage(response.data.assistantMessage);
        setIsEditingTemplate(false);
      } else {
        setTemplateError(response.data?.message || 'Failed to get template response from N2');
      }
    } catch (error) {
      console.error('Error getting template response:', error);
      setTemplateError('Failed to get template response');
    } finally {
      setIsLoadingProposedMessage(false);
    }
  };

  const handleSendTemplateMessage = async () => {
    if (!leadId || !selectedIntent || !proposedMessage) return;

    setIsSendingTemplateMessage(true);
    setTemplateError(null);

    try {
      const { api } = await import('../services/api');
      const trimmedEdit = editedMessage.trim();
      const response = await api.post('/template-response/send', {
        leadId,
        intent: selectedIntent.intentName,
        customMessage: trimmedEdit && trimmedEdit !== proposedMessage ? trimmedEdit : undefined
      });

      if (response.data.messageId) {
        // Refresh conversation to show the new message
        fetchConversationForLead(leadId, { force: true });
        handleCloseIntentModal();
      } else {
        setTemplateError(response.data?.message || 'Failed to send template message');
      }
    } catch (error) {
      console.error('Error sending template message:', error);
      setTemplateError('Failed to send template message');
    } finally {
      setIsSendingTemplateMessage(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white relative">
      {/* Inline Error Display */}
      {inlineError && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle size={16} className="text-red-600" />
            <span className="text-sm text-red-700">{inlineError}</span>
          </div>
          <button
            type="button"
            onClick={() => setInlineError(null)}
            className="text-red-500 hover:text-red-700"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={() => {
          const nearBottom = isUserNearBottom();
          setIsAtBottom(nearBottom);
          if (pendingNewMessages > 0 && nearBottom) {
            setPendingNewMessages(0);
          }
        }}
        className="flex-1 overflow-y-auto px-4 py-5 space-y-6"
      >
        {allMessages.length === 0 ? (
          isLoadingConversations ? (
            <div className="text-center text-gray-500 py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p>Loading messages...</p>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Bot size={48} className="mx-auto mb-4 text-gray-400" />
              <p>No messages yet</p>
            </div>
          )
        ) : (
          Object.entries(messageGroups).map(([date, messages]) => {
            // Hide date separator if group contains any optimistic pending messages
            const hasOptimisticPending = messages.some(msg =>
              msg.isOptimistic && msg.status === 'sending'
            );

            return (
            <div key={date}>
              {/* Date Separator - hide if any optimistic pending messages in group */}
              {!hasOptimisticPending && (
                <div className="flex items-center justify-center my-4">
                  <div className="bg-gray-100 text-gray-600 text-xs font-medium px-3 py-1 rounded-full">
                    {formatDate(messages[0].timestamp)}
                  </div>
                </div>
              )}

              {/* Messages for this date — generous gap so the status/N2/Transcript
                  badges (absolutely positioned below each bubble) never overlap
                  the next message */}
              <div className="space-y-7">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex items-start max-w-xs lg:max-w-md ${
                        message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      {/* Avatar */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        message.sender === 'user'
                          ? 'bg-blue-500 ml-2'
                          : 'bg-gray-300 mr-2'
                      }`}>
                        {message.sender === 'user' ? (
                          <User size={16} className="text-white" />
                        ) : (
                          <Bot size={16} className="text-gray-600" />
                        )}
                      </div>

                      {/* Message Bubble */}
                      <div
                        className={`px-4 py-2 relative ${
                          message.scheduledSendTime
                            ? message.sender === 'user'
                              ? 'bg-blue-500 text-white border-2 border-yellow-400 rounded-2xl'
                              : 'bg-gray-100 text-gray-900 border-2 border-yellow-400 rounded-2xl'
                            : message.isSimulated
                            ? message.sender === 'user'
                              ? 'bg-blue-500 text-white border-2 border-orange-500 rounded-2xl'
                              : 'bg-gray-100 text-gray-900 border-2 border-orange-500 rounded-2xl'
                            : message.sender === 'user'
                            ? 'bg-blue-500 text-white rounded-lg'
                            : 'bg-gray-100 text-gray-900 rounded-lg'
                        }`}
                      >
                        {/* Message Status Indicator */}
                      {message.isOptimistic && (
                        <div className="absolute -top-2 -right-2 flex items-center">
                          {message.status === 'sending' && (
                            <div className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
                              <Clock size={8} className="mr-1 animate-spin" />
                              Sending
                            </div>
                          )}
                          {message.status === 'failed' && (
                            <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
                              <AlertCircle size={8} className="mr-1" />
                              Failed
                            </div>
                          )}
                        </div>
                      )}

                      <div className={`absolute -bottom-3 ${message.sender === 'user' ? '-left-2' : '-right-2'} flex gap-1`}>
                        {isAdmin && (message.n2RequestJson || message.n2ResponseJson) && (
                          <button
                            type="button"
                            onClick={() => setSelectedN2Payload(message)}
                            className={`text-white text-xs px-2 py-1 rounded-full flex items-center shadow ${
                              message.n2ResponseStatus === 'SUCCESS'
                                ? message.sender === 'user'
                                  ? 'bg-green-600 hover:bg-green-700'
                                  : 'bg-gray-600 hover:bg-gray-700'
                                : message.n2ResponseStatus === 'FAILED'
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                            title="View N2 request/response"
                          >
                            <ChevronLeft size={8} className="mr-1" />
                            <ChevronRight size={8} className="-ml-1.5" />
                            N2
                          </button>
                        )}
                        {message.errorMessage && (
                          <button
                            type="button"
                            onClick={() => setSelectedHandoffError(message.errorMessage)}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 rounded-full flex items-center shadow"
                            title="View handoff error"
                          >
                            <AlertCircle size={8} className="mr-1" />
                            Error
                          </button>
                        )}
                        {message.sender === 'handoff' && (
                          <button
                            type="button"
                            onClick={handleDownloadTranscript}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded-full flex items-center shadow"
                            title="Download transcript"
                          >
                            <Download size={8} className="mr-1" />
                            Transcript
                          </button>
                        )}
                        {message.scheduledSendTime && (
                          <LogModeMessageStatus
                            scheduledSendTime={message.scheduledSendTime}
                            logModeStatus={message.logModeStatus}
                            isSimulated={message.isSimulated}
                            messageId={message.id}
                            isAdmin={isAdmin}
                            onLogModeAction={handleLogModeAction}
                          />
                        )}
                        {isAdmin && message.originalMessages && (
                          <button
                            type="button"
                            onClick={() => {
                              try {
                                const parsed = JSON.parse(message.originalMessages);
                                setSelectedN2Payload({
                                  n2RequestJson: JSON.stringify(parsed, null, 2),
                                  n2ResponseJson: null,
                                  isAccumulated: true
                                });
                              } catch (e) {
                                console.error('Error parsing original messages:', e);
                                setSelectedN2Payload({
                                  n2RequestJson: message.originalMessages,
                                  n2ResponseJson: null,
                                  isAccumulated: true
                                });
                              }
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 py-1 rounded-full flex items-center shadow"
                            title="View original messages"
                          >
                            <ChevronLeft size={8} className="mr-1" />
                            <ChevronRight size={8} className="-ml-1.5" />
                            Original
                          </button>
                        )}
                      </div>
                        {message.templateIntent && (
                          <div className="flex items-center gap-1 mb-1">
                            <Bot size={10} className="text-purple-600" />
                            <span className="text-xs text-purple-600 font-medium">
                              Template: {message.templateIntent}
                            </span>
                          </div>
                        )}
                        <p className="text-xs leading-relaxed whitespace-pre-wrap break-words">
                          {message.message}
                        </p>
                        {messageIdsWithVisibleTime.has(message.id) && (
                          <p
                            className={`text-xs mt-1 ${
                              message.sender === 'user'
                                ? 'text-blue-100'
                                : 'text-gray-500'
                            }`}
                          >
                            {formatTime(message.timestamp)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            );
          })
        )}
        {isAiThinking && (
          <div className="flex justify-start">
            <div className="flex items-start max-w-xs lg:max-w-md">
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-300 mr-2">
                <Bot size={16} className="text-gray-600" />
              </div>
              <div className="px-4 py-3 rounded-lg bg-gray-100 text-gray-900 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {allMessages.length > 0 && !isAtBottom && (
        pendingNewMessages > 0 ? (
          <button
            onClick={() => {
              scrollToBottom(true);
              setPendingNewMessages(0);
            }}
            className={`absolute ${isAdmin ? 'bottom-36' : 'bottom-24'} right-4 flex items-center gap-1.5 bg-blue-500 text-white text-xs font-medium pl-3 pr-2.5 py-1.5 rounded-full shadow-lg hover:bg-blue-600 transition-colors z-10`}
            title="Scroll to bottom"
          >
            {pendingNewMessages} new message{pendingNewMessages > 1 ? 's' : ''}
            <ChevronDown size={14} />
          </button>
        ) : (
          <button
            onClick={scrollToBottom}
            className={`absolute ${isAdmin ? 'bottom-36' : 'bottom-24'} right-4 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors z-10`}
            title="Scroll to bottom"
          >
            <ChevronDown size={16} />
          </button>
        )
      )}

      {/* Footer toolbar: message count on the left, actions on the right.
          The AI on/off switch lives in the widget header and the exports sit
          in a Downloads menu, so this row only holds things you actually do. */}
      <div className="px-4 py-2 border-t border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
            {allMessages.length} {allMessages.length === 1 ? 'message' : 'messages'}
          </span>
          <div className="flex items-center justify-end gap-1.5 flex-wrap">
            {isAdmin && !aiToggleInHeader && (
              <button
                type="button"
                onClick={handleToggleN2}
                disabled={isTogglingN2}
                role="switch"
                aria-checked={n2Enabled}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border transition-colors disabled:opacity-60 ${
                  n2Enabled
                    ? 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title={n2Enabled ? 'AI is replying - click to pause' : 'AI is paused - click to resume'}
              >
                <span className={`w-2 h-2 rounded-full ${n2Enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                AI
              </button>
            )}
            {isAdmin && scheduledTasks.length > 0 && (
              <button
                type="button"
                onClick={() => setShowScheduledTasksModal(true)}
                className="inline-flex items-center gap-1 h-7 px-2.5 text-xs font-medium rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                title={
                  nextScheduledTask
                    ? `${scheduledTasks.length} scheduled task(s). Next: ${formatZonedDateTime(nextScheduledTask.scheduledTime, storeTimeZone)} (in ${formatRemainingTime(nextScheduledTask.scheduledTime, new Date(now))})`
                    : `${scheduledTasks.length} scheduled task(s)`
                }
              >
                <Clock size={13} />
                {scheduledTasks.length} scheduled
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  setSendToAsd(false);
                  setShowManualHandoffModal(true);
                }}
                className="inline-flex items-center gap-1 h-7 px-2.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                title="Hand this conversation to a person at the store"
              >
                <UserCheck size={13} className="text-gray-500" />
                Handoff
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowIntentModal(true)}
                className="inline-flex items-center gap-1 h-7 px-2.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                title="Pick an intent and generate a reply from its template"
              >
                <Bot size={13} className="text-gray-500" />
                Intent
              </button>
            )}
            <div className="relative" ref={downloadMenuRef}>
              <button
                type="button"
                onClick={() => setShowDownloadMenu((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={showDownloadMenu}
                className="inline-flex items-center gap-0.5 h-7 px-2 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                title="Downloads"
              >
                <Download size={13} className="text-gray-500" />
                <ChevronDown size={12} className="text-gray-400" />
              </button>
              {showDownloadMenu && (
                <div
                  role="menu"
                  className="absolute bottom-full right-0 mb-1 w-60 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowDownloadMenu(false);
                      handleDownloadTranscript();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Download size={14} className="text-gray-400" />
                    Conversation transcript (PDF)
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setShowDownloadMenu(false);
                        handleDownloadN2Payloads();
                      }}
                      disabled={n2PayloadMessages.length === 0}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:hover:bg-white disabled:cursor-not-allowed"
                      title={n2PayloadMessages.length === 0 ? 'No N2 payloads for this lead yet' : undefined}
                    >
                      <Code size={14} className="text-gray-400" />
                      <span className="flex-1">N2 payloads (JSON)</span>
                      <span className="text-xs text-gray-400">{n2PayloadMessages.length}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Message Input Area */}
      {selectedLead && (
        <div className="border-t border-gray-200 bg-white flex-shrink-0">
          {/* Send mode - whether AI replies really reach the customer. Shown as a
              full-width strip because getting this wrong texts a real person. */}
          {isAdmin && (
            <div
              className={`flex items-center justify-between gap-2 px-4 py-1.5 text-xs border-b ${
                sendRealMessage
                  ? 'bg-red-50 text-red-800 border-red-100'
                  : 'bg-amber-50 text-amber-800 border-amber-100'
              }`}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    sendRealMessage ? 'bg-red-500 animate-pulse' : 'bg-amber-500'
                  }`}
                />
                <span className="truncate">
                  {sendRealMessage ? (
                    <><strong className="font-semibold">Live</strong> - AI replies go to the customer's phone</>
                  ) : (
                    <><strong className="font-semibold">Simulation</strong> - nothing is sent to the customer</>
                  )}
                </span>
              </span>
              <button
                type="button"
                onClick={handleToggleRealSms}
                className="flex-shrink-0 font-semibold underline underline-offset-2 hover:no-underline"
                title={sendRealMessage ? 'Stop sending real SMS for this lead' : 'Start sending real SMS for this lead'}
              >
                {sendRealMessage ? 'Switch to simulation' : 'Go live'}
              </button>
            </div>
          )}
          <div className="px-4 py-3">
            <div className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <textarea
                  ref={messageInputRef}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Simulate a customer reply…"
                  disabled={isSending}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  rows={2}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || isSending || !selectedLead}
                className={`flex items-center px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  messageInput.trim() && !isSending && selectedLead
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Send
                  </>
                )}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-400">Enter to send · Shift+Enter for a new line</p>
          </div>
        </div>
      )}
      {isAdmin && selectedN2Payload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">
                {selectedN2Payload.isAccumulated ? 'Original Messages' : 'N2 Request / Response'}
              </h4>
              <button
                type="button"
                onClick={() => setSelectedN2Payload(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            {selectedN2Payload.isAccumulated ? (
              <div className="overflow-y-auto p-4">
                <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-auto whitespace-pre-wrap max-h-[75vh]">
                  {formatJsonPayload(selectedN2Payload.n2RequestJson) || 'No accumulated messages stored'}
                </pre>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto p-4">
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Request</h5>
                  <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-auto whitespace-pre-wrap max-h-[60vh]">
                    {formatJsonPayload(selectedN2Payload.n2RequestJson) || 'No N2 request stored'}
                  </pre>
                </div>
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Response</h5>
                  <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-auto whitespace-pre-wrap max-h-[60vh]">
                    {formatJsonPayload(selectedN2Payload.n2ResponseJson) || 'No N2 response stored'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {selectedHandoffError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">Handoff Error</h4>
              <button
                type="button"
                onClick={() => setSelectedHandoffError(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-auto whitespace-pre-wrap max-h-[60vh]">
                {selectedHandoffError}
              </pre>
            </div>
          </div>
        </div>
      )}
      {showManualHandoffModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">Manual Handoff</h4>
              <button
                type="button"
                onClick={() => setShowManualHandoffModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={handoffDescription}
                  onChange={(e) => setHandoffDescription(e.target.value)}
                  placeholder="Enter the reason for this manual handoff..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows={3}
                  disabled={isProcessingHandoff}
                />
              </div>
              <div className="space-y-2 mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendToAsd}
                    onChange={(e) => setSendToAsd(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    disabled={isProcessingHandoff}
                  />
                  <span className="ml-2 text-sm text-gray-700">Send to ASD</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => handleEmailCheckboxChange(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    disabled={isProcessingHandoff}
                  />
                  <span className="ml-2 text-sm text-gray-700">Send email to sales representatives</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={includeTranscript}
                    onChange={(e) => handleTranscriptCheckboxChange(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    disabled={isProcessingHandoff || !sendEmail}
                  />
                  <span className="ml-2 text-sm text-gray-700">Include transcript in email</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    disabled={isProcessingHandoff}
                  />
                  <span className="ml-2 text-sm text-gray-700">Send SMS to customer</span>
                </label>
                {sendToAsd && (
                  <p className="ml-6 text-xs text-gray-500">
                    {sendEmail
                      ? 'One email to the ASD contacts, with the sales reps in CC.'
                      : 'One email to the ASD contacts only.'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowManualHandoffModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isProcessingHandoff}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleManualHandoff}
                disabled={isProcessingHandoff}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessingHandoff ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Trigger Handoff'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showScheduledTasksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">Scheduled Tasks</h4>
              <button
                type="button"
                onClick={() => setShowScheduledTasksModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {scheduledTasks.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No scheduled tasks for this lead
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledTasks.map((task) => (
                    <div key={task.jobKey} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              task.status === 'WAITING' ? 'bg-yellow-100 text-yellow-800' :
                              task.status === 'PAUSED' ? 'bg-orange-100 text-orange-800' :
                              task.status === 'ACQUIRED' ? 'bg-blue-100 text-blue-800' :
                              task.status === 'BLOCKED' ? 'bg-red-100 text-red-800' :
                              task.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.status}
                            </span>
                            <span className="text-xs text-gray-500">
                              {task.jobGroup === 'log-mode-jobs' ? 'Log-Mode Message' : 'Cancel Appointment'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">
                            <div className="flex items-center gap-1">
                              <Clock size={12} className="text-gray-400" />
                              Scheduled: {formatZonedDateTime(task.scheduledTime, storeTimeZone)}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <Clock size={12} className="text-gray-400" />
                              Remaining: in {formatRemainingTime(task.scheduledTime, new Date(now))}
                            </div>
                          </div>
                        </div>
                        {task.status === 'WAITING' && (
                          <button
                            type="button"
                            onClick={() => handleCancelScheduledTask(task)}
                            className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                            title="Cancel this task"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowScheduledTasksModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showIntentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={(e) => { if (e.target === e.currentTarget && !isLoadingProposedMessage && !isSendingTemplateMessage) handleCloseIntentModal(); }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col h-[500px] max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="bg-purple-100 p-2 rounded-full mr-3">
                  <Bot className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-gray-900">Send Template Message</h4>
                  <p className="text-xs text-gray-500">Choose a category and intent to preview the message.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseIntentModal}
                disabled={isSendingTemplateMessage}
                className="text-gray-400 hover:text-gray-600 disabled:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 flex-1 flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <SearchableDropdown
                    value={categoryOptions.find((o) => o.value === selectedCategory) || null}
                    options={categoryOptions}
                    onSelect={handleCategorySelect}
                    labelKey="label"
                    placeholder="All Categories"
                    searchable={false}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Intent
                  </label>
                  <SearchableDropdown
                    value={selectedIntent}
                    options={filteredIntents}
                    onSelect={handleIntentSelect}
                    labelKey="intentName"
                    placeholder={!filteredIntents.length ? 'No intents available' : 'Search or select an intent'}
                    disabled={!filteredIntents.length}
                    emptyText="No matching intents"
                  />
                </div>
              </div>
              <div className="h-48">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Message Preview
                  </label>
                  <div className="flex items-center gap-2">
                    {(templateN2Payload?.n2RequestJson || templateN2Payload?.n2ResponseJson) && (
                      <button
                        type="button"
                        onClick={() => setSelectedN2Payload(templateN2Payload)}
                        className="bg-gray-700 hover:bg-gray-800 text-white text-xs px-2 py-1 rounded-full flex items-center shadow"
                        title="View N2 request/response"
                      >
                        <ChevronLeft size={8} className="mr-1" />
                        <ChevronRight size={8} className="-ml-1.5" />
                        N2
                      </button>
                    )}
                    {proposedMessage && (
                      <span className="text-xs text-green-600 flex items-center">
                        <Check size={12} className="mr-1" />
                        Ready to send
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-full border border-gray-200 rounded-lg bg-white p-4 overflow-y-auto">
                  {isLoadingProposedMessage ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 text-purple-600 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Generating message...</p>
                      </div>
                    </div>
                  ) : templateError ? (
                    <div className="h-full flex flex-col items-center justify-center text-center px-2">
                      <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
                      <p className="text-sm text-red-700 whitespace-pre-wrap">{templateError}</p>
                      <p className="text-xs text-gray-500 mt-1">Try selecting a different intent.</p>
                    </div>
                  ) : proposedMessage ? (
                    <div className="flex flex-col gap-3">
                      {isEditingTemplate ? (
                        <textarea
                          value={editedMessage}
                          onChange={(e) => setEditedMessage(e.target.value)}
                          className="w-full p-3 border border-blue-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          rows={5}
                        />
                      ) : (
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <Bot size={16} className="text-purple-600" />
                          </div>
                          <div className="flex-1 bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{editedMessage || proposedMessage}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 justify-end">
                        {isEditingTemplate ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setIsEditingTemplate(false);
                                setEditedMessage(proposedMessage);
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsEditingTemplate(false)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                            >
                              Done
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsEditingTemplate(true)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                      <Bot className="w-10 h-10 mb-2 opacity-40" />
                      <p className="text-sm">Select an intent to see the generated message</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleCloseIntentModal}
                disabled={isSendingTemplateMessage}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendTemplateMessage}
                disabled={!proposedMessage || !editedMessage.trim() || isSendingTemplateMessage}
                title={!proposedMessage ? 'Select an intent and generate a message first' : ''}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
              >
                {isSendingTemplateMessage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Go Live Confirmation Modal */}
      {showGoLiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
              <h3 className="text-lg font-bold text-white">Go Live</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-700 text-sm leading-relaxed">
                AI replies for this lead will be sent as <span className="font-semibold">real SMS</span> to the customer's phone.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setShowGoLiveModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGoLive}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go Live
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// Memoize ChatBox to prevent remounting when parent re-renders with new selectedLead object
export default React.memo(ChatBox, (prevProps, nextProps) => {
  // Return true if props are equal (don't re-render)
  // Return false if props changed (re-render)

  // Allow re-render if leadId changes OR if lead-level settings change
  const leadIdChanged = prevProps.leadId !== nextProps.leadId;
  const n2EnabledChanged = prevProps.selectedLead?.n2Enabled !== nextProps.selectedLead?.n2Enabled;
  const sendRealMessageChanged = prevProps.selectedLead?.sendRealMessage !== nextProps.selectedLead?.sendRealMessage;
  const storeTimeZoneChanged = prevProps.storeTimeZone !== nextProps.storeTimeZone;

  // Return true if leadId and lead settings are the same (skip re-render)
  // Return false if any changed (re-render)
  return !leadIdChanged && !n2EnabledChanged && !sendRealMessageChanged && !storeTimeZoneChanged;
});
